import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { PreviewUserDto } from 'woofwoof-api';

type OwnerAddressFields = Partial<
  Pick<PreviewUserDto, 'address' | 'zip' | 'city'>
>;

/**
 * "ul. Kwiatowa 12/3, 50-123 Wrocław", as the owner cards show it and as the
 * backend records the owner's profile address on a handed-over drug. Without
 * a street there is no address.
 */
export function ownerAddressLine(
  owner?: OwnerAddressFields | null,
): string | undefined {
  const street = owner?.address?.trim();
  if (!street) return undefined;
  const town = [owner?.zip?.trim(), owner?.city?.trim()]
    .filter(Boolean)
    .join(' ');
  return [street, town].filter(Boolean).join(', ');
}

/**
 * The owner preview the patient card (`OrganizationPatientOwnerInfo`) or the
 * visit page (`VisitPatientOwnerInfo`) already loaded, read from the cache
 * only: `enabled: false` never fetches it.
 */
function useCachedOwner(
  key: 'patient-user-details' | 'visit-patient-user-details',
  profileId?: string,
): OwnerAddressFields | undefined {
  const { data } = useQuery<AxiosResponse<PreviewUserDto>>({
    queryKey: [key, profileId],
    enabled: false,
  });
  return profileId ? data?.data : undefined;
}

/**
 * #688 R1-2: the address a handed-over Rx drug's recipient field starts with,
 * when the owner's data is already on screen; otherwise undefined and the
 * backend falls back to the owner's profile itself.
 */
export function useOwnerAddress(profileId?: string): string | undefined {
  const patientCard = useCachedOwner('patient-user-details', profileId);
  const visitPage = useCachedOwner('visit-patient-user-details', profileId);
  return ownerAddressLine(patientCard) ?? ownerAddressLine(visitPage);
}
